type SoundName =
  | 'spray'
  | 'plunger'
  | 'paper'
  | 'shower'
  | 'bubble'
  | 'toast'
  | 'quack'
  | 'quackKamikaze'
  | 'quackArmor'
  | 'quackMedic'
  | 'quackSniper'
  | 'quackBoss'
  | 'pickup'
  | 'door'
  | 'hurt'
  | 'empty'
  | 'reload'
  | 'splash'
  | 'sprint'
  | 'win'
  | 'click';

const note = (root: number, semitones: number): number => root * 2 ** (semitones / 12);

export class RetroAudio {
  private context?: AudioContext;
  private master?: GainNode;
  private musicBus?: GainNode;
  private musicTimer?: number;
  private musicStep = 0;
  private musicIntensity = 0;
  private musicLevel = 0;
  private volume = 0.7;

  async init(): Promise<void> {
    if (!this.context) {
      this.context = new AudioContext();
      this.master = this.context.createGain();
      this.musicBus = this.context.createGain();
      const compressor = this.context.createDynamicsCompressor();
      compressor.threshold.value = -18;
      compressor.knee.value = 12;
      compressor.ratio.value = 5;
      compressor.attack.value = 0.004;
      compressor.release.value = 0.18;
      this.master.gain.value = this.volume * 0.32;
      this.musicBus.gain.value = 0.42;
      this.musicBus.connect(this.master);
      this.master.connect(compressor);
      compressor.connect(this.context.destination);
    }
    if (this.context.state === 'suspended') await this.context.resume();
  }

  setVolume(value: number): void {
    this.volume = Math.max(0, Math.min(1, value));
    if (this.master && this.context) {
      this.master.gain.setTargetAtTime(this.volume * 0.32, this.context.currentTime, 0.02);
    }
  }

  sfx(name: SoundName): void {
    if (!this.context || !this.master || this.volume <= 0) return;
    const presets: Record<SoundName, [number, number, OscillatorType, number]> = {
      spray: [620, 250, 'square', 0.08],
      plunger: [125, 58, 'triangle', 0.18],
      paper: [240, 72, 'square', 0.2],
      shower: [720, 310, 'sine', 0.075],
      bubble: [260, 760, 'sine', 0.16],
      toast: [95, 45, 'sawtooth', 0.22],
      quack: [180, 115, 'square', 0.18],
      quackKamikaze: [420, 85, 'sawtooth', 0.22],
      quackArmor: [105, 72, 'square', 0.24],
      quackMedic: [520, 680, 'triangle', 0.2],
      quackSniper: [880, 210, 'sine', 0.24],
      quackBoss: [82, 45, 'sawtooth', 0.42],
      pickup: [520, 1040, 'square', 0.13],
      door: [75, 42, 'sawtooth', 0.3],
      hurt: [150, 58, 'square', 0.2],
      empty: [80, 80, 'square', 0.04],
      reload: [190, 320, 'triangle', 0.18],
      splash: [410, 170, 'sine', 0.1],
      sprint: [240, 420, 'triangle', 0.09],
      win: [330, 880, 'square', 0.45],
      click: [240, 180, 'square', 0.04],
    };
    const [from, to, type, duration] = presets[name];
    this.tone(from, to, duration, type, name === 'hurt' || name === 'toast' ? 0.36 : 0.22, this.master);
    if (name === 'spray' || name === 'plunger' || name === 'paper' || name === 'shower' || name === 'toast' || name === 'splash' || name === 'quackKamikaze' || name === 'quackBoss') {
      this.noise(duration * 0.7, name === 'toast' ? 0.22 : 0.08, this.master);
    }
    if (name === 'reload') {
      this.tone(330, 520, 0.08, 'square', 0.12, this.master, 0.13);
    }
  }

  startMusic(level = 0): void {
    this.musicLevel = level;
    if (this.musicTimer) return;
    this.musicStep = 0;
    this.musicTimer = window.setInterval(() => this.tickMusic(), 125);
  }

  setMusicState(level: number, intensity: number): void {
    this.musicLevel = Math.max(0, Math.min(2, level));
    this.musicIntensity = Math.max(0, Math.min(1, intensity));
    if (this.musicBus && this.context) {
      this.musicBus.gain.setTargetAtTime(0.34 + this.musicIntensity * 0.22, this.context.currentTime, 0.12);
    }
  }

  stopMusic(): void {
    if (this.musicTimer) window.clearInterval(this.musicTimer);
    this.musicTimer = undefined;
    this.musicStep = 0;
    this.musicIntensity = 0;
  }

  private tickMusic(): void {
    if (!this.context || !this.musicBus || this.volume <= 0) return;
    const themes = [
      { root: 55, scale: [0, 3, 5, 7, 10], bass: [0, 0, 3, 0, 5, 3, -2, 0] },
      { root: 61.74, scale: [0, 2, 5, 7, 9], bass: [0, -3, 0, 5, 2, 0, 7, 5] },
      { root: 51.91, scale: [0, 3, 6, 8, 10], bass: [0, 0, 6, 3, -2, 3, 8, 6] },
    ] as const;
    const theme = themes[this.musicLevel];
    const step = this.musicStep % 32;
    const phrase = Math.floor(this.musicStep / 32) % 4;
    const bassStep = Math.floor(step / 2) % theme.bass.length;

    if (step % 2 === 0) {
      const bassNote = note(theme.root, theme.bass[bassStep] + (phrase === 3 ? 5 : 0));
      this.tone(bassNote, bassNote * 0.985, 0.17, 'square', 0.075, this.musicBus);
    }
    if (step % 4 === 0) {
      this.tone(105, 42, 0.1, 'sine', 0.12, this.musicBus);
    }
    if (step % 8 === 4) {
      this.noise(0.07, 0.055 + this.musicIntensity * 0.035, this.musicBus);
      this.tone(190, 120, 0.055, 'triangle', 0.035, this.musicBus);
    }
    if (step % 2 === 1 && (this.musicIntensity > 0.18 || phrase % 2 === 1)) {
      this.noise(0.018, 0.018 + this.musicIntensity * 0.018, this.musicBus);
    }

    const melodicStep = (Math.floor(step / 2) + phrase * 2) % theme.scale.length;
    if (step % 4 === 2 || (this.musicIntensity > 0.55 && step % 2 === 0)) {
      const leadNote = note(theme.root * 4, theme.scale[melodicStep] + (phrase === 2 ? 12 : 0));
      this.tone(leadNote, leadNote * 1.006, 0.1, phrase % 2 ? 'triangle' : 'square', 0.025 + this.musicIntensity * 0.025, this.musicBus);
    }
    if (step === 0 || step === 16) {
      const chordRoot = note(theme.root * 2, phrase === 2 ? 3 : 0);
      this.tone(chordRoot, chordRoot * 0.997, 0.7, 'sawtooth', 0.018, this.musicBus);
      this.tone(note(chordRoot, 7), note(chordRoot, 7) * 0.997, 0.7, 'triangle', 0.014, this.musicBus);
    }
    if (step === 31) {
      this.tone(note(theme.root * 2, theme.scale[(phrase + 2) % theme.scale.length]), theme.root, 0.16, 'square', 0.04, this.musicBus);
    }
    this.musicStep += 1;
  }

  private tone(
    from: number,
    to: number,
    duration: number,
    type: OscillatorType,
    gainAmount: number,
    destination: AudioNode,
    delay = 0,
  ): void {
    if (!this.context) return;
    const now = this.context.currentTime + delay;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(Math.max(20, from), now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, to), now + duration);
    gain.gain.setValueAtTime(gainAmount, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain);
    gain.connect(destination);
    oscillator.start(now);
    oscillator.stop(now + duration);
  }

  private noise(duration: number, gainAmount: number, destination: AudioNode): void {
    if (!this.context) return;
    const samples = Math.ceil(this.context.sampleRate * duration);
    const buffer = this.context.createBuffer(1, samples, this.context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < samples; i += 1) data[i] = Math.random() * 2 - 1;
    const source = this.context.createBufferSource();
    const gain = this.context.createGain();
    source.buffer = buffer;
    gain.gain.setValueAtTime(gainAmount, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, this.context.currentTime + duration);
    source.connect(gain);
    gain.connect(destination);
    source.start();
  }
}
